import React, { useState } from 'react';

import {
    FormControl,
    Flex,
    FormLabel,
    Heading,
    Menu,
    MenuButton,
    MenuList,
    MenuOptionGroup,
    MenuItemOption,
    MenuGroup,
    MenuItem,
    MenuDivider,
    Button,
    Badge,
    Box,
} from '@chakra-ui/react';
import { Field, FieldProps, useField } from 'formik';
import { HiOutlineChevronDown } from 'react-icons/hi2';

import { CustomBadge } from '@components/custom-badge';
import { CustomText } from '@components/Custom-text';

interface FormControlMultiSelectFieldProps {
    name: string;
    label: string;
    placeholder: string;
    options: string[];
    isRequired?: boolean;
    minWidth?: number;
}

const FormControlMultiSelectField = ({
    name,
    label,
    placeholder,
    options,
    isRequired,
    minWidth = 400,
}: FormControlMultiSelectFieldProps) => {
    const [{ value: selectedOptions }] = useField<string[]>(name);
    const [isOpen, setIsOpen] = useState(false);
    return (
        <Field name={name}>
            {({ field, form: { touched, errors, setFieldValue, setTouched } }: FieldProps) => {
                const isInvalid = !!errors[field.name] && touched[field.name];
                return (
                    <FormControl isInvalid={Boolean(isInvalid)} minWidth="100%">
                        <Flex>
                            <FormLabel>
                                <Heading color="titleText" size="xs" as="h6">
                                    {label}
                                </Heading>
                            </FormLabel>
                            {isRequired ? (
                                <Heading color="red" size="xs" ml={-2}>
                                    *
                                </Heading>
                            ) : null}
                        </Flex>
                        <Menu closeOnSelect={false}>
                            {({ onClose }) => (
                                <Box minWidth="100%" width="100%">
                                    <MenuButton
                                        width="100%"
                                        height="2.5rem"
                                        border={isInvalid ? 2 : 1}
                                        px="1rem"
                                        name={name}
                                        as={Button}
                                        variant="outline"
                                        justifyContent="space-between"
                                        borderColor={isInvalid ? 'red.500' : 'inherit'}
                                        borderStyle="solid"
                                        borderRadius="0.375rem"
                                        bg="transparent"
                                        _hover={{ bg: 'transparent' }}
                                        rightIcon={<HiOutlineChevronDown />}
                                        className="multiselect-button"
                                        _groupHover={{ bg: 'transparent' }}
                                        onClick={() => setIsOpen(true)}
                                    >
                                        {selectedOptions?.length > 0 ? (
                                            <Flex gap={1} maxWidth="100%" overflow="hidden">
                                                <CustomBadge selectionOptions={selectedOptions} />
                                                {selectedOptions.length > 3 ? (
                                                    <Badge p={2} borderRadius={10}>
                                                        <CustomText color="titleText" weight={500}>
                                                            ...
                                                        </CustomText>
                                                    </Badge>
                                                ) : null}
                                            </Flex>
                                        ) : (
                                            <CustomText color="subText" size="md">
                                                {placeholder}
                                            </CustomText>
                                        )}
                                    </MenuButton>
                                    <MenuList
                                        flex={1}
                                        minWidth={minWidth}
                                        // maxHeight={250}
                                        overflowY="scroll"
                                        style={{ maxHeight: isOpen ? 250 : 0 }}
                                        onBlur={() => setTouched({ [field.name]: true })}
                                    >
                                        <MenuGroup title={undefined}>
                                            <MenuItem
                                                onClick={() => {
                                                    setFieldValue(name, []);
                                                    onClose();
                                                }}
                                            >
                                                Clear all
                                            </MenuItem>
                                        </MenuGroup>
                                        <MenuDivider />
                                        <MenuOptionGroup
                                            type="checkbox"
                                            title={undefined}
                                            key={selectedOptions[0]}
                                            flex={1}
                                            defaultValue={selectedOptions}
                                            onChange={(values: string | string[]) => {
                                                if (Array.isArray(values)) {
                                                    const filteredValues = values.filter(
                                                        item => item.length
                                                    );
                                                    setFieldValue(name, filteredValues);
                                                }
                                            }}
                                        >
                                            {options.map(option => (
                                                <MenuItemOption
                                                    // @ts-ignore <MenuItemOption> does have a 'type' prop because it is just a button. This is to make sure clicking this doesn't submit any forms.
                                                    type="button"
                                                    key={option}
                                                    value={option}
                                                >
                                                    {option}
                                                </MenuItemOption>
                                            ))}
                                        </MenuOptionGroup>
                                    </MenuList>
                                </Box>
                            )}
                        </Menu>
                        <CustomText size="xs" color="red.500">
                            {isInvalid && JSON.stringify(errors[field.name])?.replaceAll('"', '')}
                        </CustomText>
                    </FormControl>
                );
            }}
        </Field>
    );
};

export default FormControlMultiSelectField;
